import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type BisScreenStatus,
  type GenerateProformaResponse,
  type ProformaInvoice,
  type CommitProformaResponse,
  type PatchProformaResponse,
  type GetProformaResponse,
  type ListPaymentAccountsResponse,
  type ClientQuote,
} from '@/lib/api';
import { ShieldAlert } from 'lucide-react';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  clientQuote: ClientQuote;
  rfqId?: string;
  /**
   * Canonical PO# captured on the RFQ (Step 3 first-class event).
   * When set, the PO# input prefills with this value so the broker
   * doesn't retype something they've already entered on the RFQ.
   */
  rfqCustomerPoNumber?: string | null;
  /**
   * Customer's BIS/OFAC screen status + ISO country code (Step 4).
   * Used to surface a soft warning when an international customer isn't
   * `clear` — broker can still proceed; the warning is informational.
   */
  customerCountryCode?: string | null;
  customerBisScreenStatus?: BisScreenStatus | null;
  /**
   * When set, the modal opens on this EXISTING proforma instead of generating a
   * new one — the path to edit a draft saved in a previous session. Its full
   * detail (incl. html_snapshot) is fetched on open so the editor is seeded.
   */
  editProformaId?: string | null;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function ProformaInvoiceModal({
  open,
  onClose,
  clientQuote,
  rfqId,
  rfqCustomerPoNumber,
  customerCountryCode,
  customerBisScreenStatus,
  editProformaId,
}: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const [customerPoNumber, setCustomerPoNumber] = useState('');
  const [validityDays, setValidityDays] = useState<number>(30);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('wire');
  const [paymentReference, setPaymentReference] = useState('');
  // Receiving account printed in the wire box (broker picks per proforma).
  const [paymentAccountId, setPaymentAccountId] = useState<string>('');
  // Toggles the draft back into an editable form (regenerate in place).
  const [editingDraft, setEditingDraft] = useState(false);

  const [preview, setPreview] = useState<GenerateProformaResponse | null>(null);
  const [saved, setSaved] = useState<ProformaInvoice | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Receiving accounts are tenant-managed (Billing page), loaded on open.
  const accountsQuery = useQuery({
    queryKey: ['payment-accounts'],
    queryFn: () =>
      api<ListPaymentAccountsResponse>('/api/billing/payment-accounts', {
        tokenGetter: () => getToken(),
      }),
    enabled: open,
    staleTime: 60_000,
  });
  const paymentAccounts = accountsQuery.data?.accounts ?? [];

  // Once accounts load for a NEW proforma, preselect the tenant's default so the
  // dropdown isn't empty. Don't touch an explicit choice or an edit-seeded value.
  useEffect(() => {
    if (!open || editProformaId || paymentAccountId || paymentAccounts.length === 0) return;
    const def = paymentAccounts.find((a) => a.isDefault) ?? paymentAccounts[0]!;
    setPaymentAccountId(def.id);
  }, [open, editProformaId, paymentAccountId, paymentAccounts]);

  // Editing an existing proforma? Fetch its full detail (incl. html_snapshot).
  const editQuery = useQuery({
    queryKey: ['proforma-detail', editProformaId],
    queryFn: () =>
      api<GetProformaResponse>(`/api/proforma-invoices/${editProformaId}`, {
        tokenGetter: () => getToken(),
      }),
    enabled: open && !!editProformaId,
    staleTime: 0,
  });

  // Seed the editor from the fetched proforma, then drop a draft straight into
  // edit mode — this is the path to fix a draft saved in a previous session.
  useEffect(() => {
    const pi = editQuery.data?.proformaInvoice;
    if (!open || !editProformaId || !pi) return;
    setSaved(pi);
    if (pi.html) {
      setPreview({
        proformaRef: pi.ref,
        quoteRef: pi.quoteRef,
        rfqRef: pi.rfqRef,
        paymentAccountId: pi.paymentAccountId,
        generatedAt: pi.generatedAt,
        validUntil: pi.validUntil,
        currency: pi.currency,
        qty: pi.qty,
        unitPriceCents: pi.unitPriceCents,
        totalCents: pi.totalCents,
        paymentTerms: pi.paymentTerms ?? '',
        html: pi.html,
      });
    }
    setCustomerPoNumber(pi.customerPoNumber ?? '');
    setPaymentTerms(pi.paymentTerms ?? '');
    setPaymentAccountId(pi.paymentAccountId ?? '');
    setValidityDays(
      Math.max(
        1,
        Math.round(
          (new Date(pi.validUntil).getTime() - new Date(pi.generatedAt).getTime()) / 86_400_000,
        ),
      ),
    );
    if (pi.status === 'draft') setEditingDraft(true);
  }, [open, editProformaId, editQuery.data]);

  useEffect(() => {
    if (open) {
      // When opening to edit an existing proforma, the fetch effect above seeds
      // everything — don't stomp it with the new-proforma prefill.
      if (editProformaId) return;
      // Prefill PO# from the RFQ's canonical customer_po_number on open.
      // Broker can still override; sending blank falls back server-side to the
      // same value via buildProformaFromInputs — this is purely a visibility
      // improvement so the broker sees what'll snapshot on the proforma.
      setCustomerPoNumber(rfqCustomerPoNumber ?? '');
    } else {
      setCustomerPoNumber('');
      setValidityDays(30);
      setPaymentTerms('');
      setPaymentMethod('wire');
      setPaymentReference('');
      setPaymentAccountId('');
      setEditingDraft(false);
      setPreview(null);
      setSaved(null);
    }
  }, [open, rfqCustomerPoNumber]);

  function invalidateLists() {
    qc.invalidateQueries({ queryKey: ['proforma-invoices'] });
    qc.invalidateQueries({ queryKey: ['client-quotes'] });
    if (rfqId) qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  const previewMutation = useMutation({
    mutationFn: async () => {
      return api<GenerateProformaResponse>('/api/proforma-invoices/generate', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          clientQuotePublicId: clientQuote.id,
          customerPoNumber: customerPoNumber || undefined,
          validityDays,
          paymentTerms: paymentTerms || undefined,
          paymentAccountId,
        }),
      });
    },
    onSuccess: (data) => {
      setPreview(data);
      setSaved(null);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api<CommitProformaResponse>('/api/proforma-invoices', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          clientQuotePublicId: clientQuote.id,
          customerPoNumber: customerPoNumber || undefined,
          validityDays,
          paymentTerms: paymentTerms || undefined,
          paymentAccountId,
        }),
      });
    },
    onSuccess: (data) => {
      setSaved(data.proformaInvoice);
      if (data.proformaInvoice.html) {
        setPreview({
          proformaRef: data.proformaInvoice.ref,
          quoteRef: data.proformaInvoice.quoteRef,
          rfqRef: data.proformaInvoice.rfqRef,
          paymentAccountId: data.proformaInvoice.paymentAccountId,
          generatedAt: data.proformaInvoice.generatedAt,
          validUntil: data.proformaInvoice.validUntil,
          currency: data.proformaInvoice.currency,
          qty: data.proformaInvoice.qty,
          unitPriceCents: data.proformaInvoice.unitPriceCents,
          totalCents: data.proformaInvoice.totalCents,
          paymentTerms: data.proformaInvoice.paymentTerms ?? '',
          html: data.proformaInvoice.html,
        });
      }
      invalidateLists();
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (newStatus: 'sent' | 'paid' | 'cancelled') => {
      if (!saved) throw new Error('No saved proforma');
      const body: Record<string, unknown> = { status: newStatus };
      if (newStatus === 'paid') {
        body.paymentMethod = paymentMethod;
        if (paymentReference) body.paymentReference = paymentReference;
      }
      return api<PatchProformaResponse>(`/api/proforma-invoices/${saved.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      setSaved(data.proformaInvoice);
      invalidateLists();
    },
  });

  // Re-render a DRAFT in place with the edited presentation fields.
  const regenerateMutation = useMutation({
    mutationFn: async () => {
      if (!saved) throw new Error('No saved proforma');
      return api<CommitProformaResponse>(`/api/proforma-invoices/${saved.id}/regenerate`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          customerPoNumber: customerPoNumber || null,
          validityDays,
          paymentTerms: paymentTerms || undefined,
          paymentAccountId,
        }),
      });
    },
    onSuccess: (data) => {
      setSaved(data.proformaInvoice);
      if (data.proformaInvoice.html) {
        setPreview((prev) => (prev ? { ...prev, html: data.proformaInvoice.html! } : prev));
      }
      setEditingDraft(false);
      invalidateLists();
    },
  });

  // Load the saved draft's values back into the form fields for editing.
  function startEditDraft() {
    if (!saved) return;
    setCustomerPoNumber(saved.customerPoNumber ?? '');
    setPaymentTerms(saved.paymentTerms ?? '');
    setPaymentAccountId(saved.paymentAccountId ?? '');
    const days = Math.max(
      1,
      Math.round(
        (new Date(saved.validUntil).getTime() - new Date(saved.generatedAt).getTime()) / 86_400_000,
      ),
    );
    setValidityDays(days);
    setEditingDraft(true);
  }

  // Render the html into the iframe whenever preview/html updates
  useEffect(() => {
    if (!preview?.html || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(preview.html);
    doc.close();
  }, [preview]);

  const downloadHtml = () => {
    if (!preview) return;
    const blob = new Blob([preview.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${preview.proformaRef}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyHtml = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview.html);
    } catch {
      window.prompt('Copy the HTML manually:', preview.html.slice(0, 100) + '…');
    }
  };

  const openInNewTab = () => {
    if (!preview) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(preview.html);
    win.document.close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Issue proforma invoice</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Confirms the order and bills the customer with wire instructions. Order ships once payment clears.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr]">
          <aside className="border-b lg:border-r border-border overflow-auto p-5 space-y-5 bg-muted/20">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Source quote
              </div>
              <div className="text-sm font-mono">{clientQuote.ref}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Status: <span className="font-medium">{clientQuote.status}</span>
              </div>
              <div className="mt-2 font-mono text-sm font-semibold text-primary">
                {fmtMoney(clientQuote.customerTotalCents, clientQuote.currency)}
                {clientQuote.qty > 1 && (
                  <span className="text-muted-foreground font-normal"> ({clientQuote.qty} ea)</span>
                )}
              </div>
            </div>

            {/*
              BIS/OFAC soft warning (Step 4 Phase A). Surfaces when the
              customer is international (country !== US) AND the screen
              status isn't 'clear'. Informational only — broker can still
              proceed. Hard block isn't appropriate at Phase A; the broker
              is the export-compliance authority on every deal.
            */}
            {!saved && customerCountryCode && customerCountryCode.toUpperCase() !== 'US' && customerBisScreenStatus !== 'clear' && (
              <div className="border border-amber-200 bg-amber-50 text-amber-900 rounded p-3 text-xs">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
                  <div className="space-y-1">
                    <div className="font-semibold">BIS / OFAC screen not on file</div>
                    <div>
                      This customer ({customerCountryCode}) hasn't been screened against the Consolidated Screening List
                      {customerBisScreenStatus === 'flagged' ? ' as clear (current status: flagged)'
                        : customerBisScreenStatus === 'review_pending' ? ' to a final result (review pending)'
                        : ''}.
                      Screen from the RFQ header before issuing.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(!saved || editingDraft) && (
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                    Customer PO # (optional)
                    {rfqCustomerPoNumber && customerPoNumber === rfqCustomerPoNumber && (
                      <span className="px-1.5 py-0.5 normal-case tracking-normal text-[10px] font-medium bg-emerald-100 text-emerald-800 rounded">
                        from RFQ
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={customerPoNumber}
                    onChange={(e) => setCustomerPoNumber(e.target.value)}
                    placeholder="PO-2024-12345"
                    maxLength={80}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Validity (days)
                  </div>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={validityDays}
                    onChange={(e) => setValidityDays(Number(e.target.value))}
                    className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Receiving account
                  </div>
                  {paymentAccounts.length > 0 ? (
                    <>
                      <select
                        value={paymentAccountId}
                        onChange={(e) => setPaymentAccountId(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                      >
                        {paymentAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.label}{a.isDefault ? ' — default' : ''}
                          </option>
                        ))}
                      </select>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {paymentAccounts.find((a) => a.id === paymentAccountId)?.hint}
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      No receiving accounts yet. The proforma will show a “details provided
                      separately” note. Add one under <span className="font-medium">Billing</span> in
                      the menu.
                    </div>
                  )}
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Payment terms (optional)
                  </div>
                  <textarea
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    placeholder="Defaults to 'Cash in advance via wire / Wise.'"
                    maxLength={500}
                    rows={3}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
              </div>
            )}

            {!preview ? (
              <button
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
                className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {previewMutation.isPending ? 'Generating…' : 'Generate preview'}
              </button>
            ) : !saved ? (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Preview</div>
                  <div className="text-sm font-mono">{preview.proformaRef}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 italic">Not saved yet.</div>
                </div>
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving…' : 'Save proforma (draft)'}
                </button>
                <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download preview .html</button>
                <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy preview HTML</button>
                <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                <button
                  onClick={() => setPreview(null)}
                  className="w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Adjust &amp; regenerate
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Saved · <span className="font-semibold text-primary">{saved.status}</span>
                  </div>
                  <div className="text-sm font-mono">{saved.ref}</div>
                  {saved.customerPoNumber && (
                    <div className="text-[11px] text-muted-foreground mt-1">Customer PO: {saved.customerPoNumber}</div>
                  )}
                  {saved.sentAt && <div className="text-[11px] text-muted-foreground mt-1">Sent {new Date(saved.sentAt).toLocaleString()}</div>}
                  {saved.paidAt && <div className="text-[11px] text-muted-foreground mt-1">Paid {new Date(saved.paidAt).toLocaleString()}</div>}
                </div>

                {saved.status === 'draft' && editingDraft && (
                  <>
                    <button
                      onClick={() => regenerateMutation.mutate()}
                      disabled={regenerateMutation.isPending}
                      className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                    >
                      {regenerateMutation.isPending ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      onClick={() => setEditingDraft(false)}
                      disabled={regenerateMutation.isPending}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </>
                )}
                {saved.status === 'draft' && !editingDraft && (
                  <>
                    <button
                      onClick={startEditDraft}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                      Edit draft
                    </button>
                    <button
                      onClick={() => patchMutation.mutate('sent')}
                      disabled={patchMutation.isPending}
                      className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                    >
                      {patchMutation.isPending ? 'Updating…' : 'Mark as sent'}
                    </button>
                  </>
                )}
                {saved.status === 'sent' && (
                  <>
                    <div className="space-y-1.5 border border-border rounded p-2 bg-background">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Payment received via
                      </div>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-full px-2 py-1 text-sm bg-background border border-border rounded"
                      >
                        <option value="wire">Wire (Revolut)</option>
                        <option value="wise">Wise</option>
                        <option value="ach">ACH</option>
                        <option value="card">Credit card</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="text"
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                        placeholder="Wire ref / txn id (optional)"
                        maxLength={200}
                        className="w-full px-2 py-1 text-sm bg-background border border-border rounded font-mono"
                      />
                    </div>
                    <button
                      onClick={() => patchMutation.mutate('paid')}
                      disabled={patchMutation.isPending}
                      className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                    >
                      {patchMutation.isPending ? 'Updating…' : 'Mark as paid'}
                    </button>
                    <button
                      onClick={() => patchMutation.mutate('cancelled')}
                      disabled={patchMutation.isPending}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                      Mark as cancelled
                    </button>
                  </>
                )}

                <div className="border-t border-border pt-2 mt-2 space-y-2">
                  <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download .html</button>
                  <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy HTML</button>
                  <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                </div>
              </div>
            )}

            {previewMutation.isError && (
              <div className="text-sm text-destructive">{(previewMutation.error as { message?: string })?.message ?? 'Generation failed'}</div>
            )}
            {saveMutation.isError && (
              <div className="text-sm text-destructive">{(saveMutation.error as { message?: string })?.message ?? 'Save failed'}</div>
            )}
            {patchMutation.isError && (
              <div className="text-sm text-destructive">{(patchMutation.error as { message?: string })?.message ?? 'Status update failed'}</div>
            )}
          </aside>

          <section className="overflow-hidden bg-[#f4f6fa] flex items-stretch min-h-[55vh] lg:min-h-0">
            {preview ? (
              <iframe ref={iframeRef} title="Proforma preview" className="w-full h-full border-0 bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                Set fields on the left and click <span className="mx-1 font-medium">Generate preview</span>.
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">Done</button>
        </div>
      </div>
    </div>
  );
}
